export const CPL_SLIDERS = [
  {
    id: 'chek',
    label: 'Средний чек',
    min: 5000,
    max: 500000,
    step: 5000,
    defaultValue: 50000,
    format: 'money',
  },
  {
    id: 'costPct',
    label: 'Себестоимость / переменные расходы',
    hint: 'Всё что вычитается из чека: материалы, зарплата мастера, доставка',
    min: 0,
    max: 85,
    step: 5,
    defaultValue: 40,
    format: 'percent',
  },
  {
    id: 'crPct',
    label: 'Конверсия продаж (заявка → оплата)',
    hint: 'Из 10 заявок сколько заканчиваются оплатой?',
    min: 5,
    max: 80,
    step: 5,
    defaultValue: 30,
    format: 'percent',
  },
  {
    id: 'sharePct',
    label: 'Доля маржи на привлечение клиента',
    hint: 'Какую часть прибыли готовы отдать за привлечение одного клиента',
    min: 5,
    max: 60,
    step: 5,
    defaultValue: 25,
    format: 'percent',
  },
  {
    id: 'ltv',
    label: 'Число покупок клиента (LTV)',
    hint: 'Сколько раз в среднем клиент покупает у вас',
    min: 1,
    max: 12,
    step: 0.5,
    defaultValue: 1,
    format: 'ltv',
    dividerAfter: true,
  },
  {
    id: 'actualCpl',
    label: 'Фактический CPL — сколько платите сейчас',
    hint: 'Реальная стоимость заявки из рекламного кабинета — от неё считается ROMI',
    min: 100,
    max: 50000,
    step: 100,
    defaultValue: 1000,
    format: 'money',
    highlight: true,
  },
];

export const CPL_FORMULA_STEPS = [
  {
    marker: '①',
    title: 'Средний чек и прибыль на продажу',
    formula: 'Средний чек = Выручка ÷ Продажи · Прибыль на продажу = (Выручка − Все расходы) ÷ Продажи',
    example: 'Пример: 350 000 ÷ 85 = <b>4 118 ₸</b> чек. (350 000 − 221 000) ÷ 85 = <b>1 518 ₸</b> прибыли с каждой продажи',
  },
  {
    marker: '②',
    title: 'CPS — сколько можно потратить на привлечение одной продажи',
    formula: 'CPS = Средний чек × Желаемая маржинальность%',
    example: 'Пример: 4 118 × 7% = <b>288 ₸</b> — максимум на маркетинг чтобы остаться с нужной маржой',
  },
  {
    marker: '③',
    title: 'CPL — стоимость лида и CPC — стоимость клика',
    formula: 'CPL = CPS × CR2 · CPC = CPL × CR1',
    example: 'Пример: 288 × 50% = <b>144 ₸</b> за лид. 144 × 5% = <b>7 ₸</b> за клик — столько может стоить клик чтобы экономика сходилась',
  },
  {
    marker: '④',
    title: 'План на следующий месяц',
    formula: 'План продаж = (Продажи ÷ Период) × 30 × (1 + Рост%) · Бюджет = План × CPL ÷ CR2',
    example: 'Пример: (85 ÷ 45) × 30 × 1.25 = <b>71 продажа</b>. 71 × 144 ÷ 50% = <b>20 448 ₸</b> рекламного бюджета',
  },
];

export const DEFAULT_CPL_INPUTS = CPL_SLIDERS.reduce((acc, slider) => {
  acc[slider.id] = slider.defaultValue;
  return acc;
}, {});

export const formatMoney = (value, locale = 'ru-RU') =>
  `${Math.round(value).toLocaleString(locale)} ₸`;

export const formatPercent = (value) => `${Math.round(value)}%`;

export const formatLtv = (value) => `×${value}`;

export const getSliderFillPercent = (value, min, max) => {
  if (max <= min) {
    return 0;
  }

  return ((value - min) / (max - min)) * 100;
};

export const calculateCplMetrics = ({ chek, costPct, crPct, sharePct, ltv, actualCpl }) => {
  const cost = costPct / 100;
  const cr = crPct / 100;
  const share = sharePct / 100;
  const margin = chek * (1 - cost) * ltv;
  const cac = margin * share;
  const cpl = cac * cr;
  const leadsPerSale = (1 / cr).toFixed(1);
  const cacActual = cr > 0 ? actualCpl / cr : 0;
  const netPerClient = margin - cacActual;
  const romiActual = cacActual > 0 ? Math.round((netPerClient / cacActual) * 100) : 0;
  const marginRate = Math.round((1 - cost) * 100);

  return {
    margin,
    cac,
    cpl,
    leadsPerSale,
    cacActual,
    netPerClient,
    romiActual,
    marginRate,
  };
};

export const getRomiPresentation = ({ actualCpl, cpl, romiActual }) => {
  if (actualCpl >= cpl) {
    return {
      isAboveLine: true,
      status: 'CPL выше красной линии',
      tone: 'bad',
    };
  }

  if (romiActual >= 150) {
    return {
      isAboveLine: false,
      displayValue: romiActual,
      status: 'отлично',
      tone: 'good',
    };
  }

  if (romiActual >= 50) {
    return {
      isAboveLine: false,
      displayValue: romiActual,
      status: 'нормально',
      tone: 'warn',
    };
  }

  return {
    isAboveLine: false,
    displayValue: romiActual,
    status: 'слабо',
    tone: 'default',
  };
};

export const getInsightState = ({
  actualCpl,
  cpl,
  crPct,
  ltv,
  sharePct,
  cac,
  romiActual,
}) => {
  if (actualCpl >= cpl) {
    return {
      variant: 'bad',
      message: 'Фактический CPL <b>{actual}</b> превышает допустимый <b>{cpl}</b>. Реклама убыточна — нужно снизить стоимость заявки или поднять конверсию продаж.',
      vars: { actual: actualCpl, cpl },
    };
  }

  if (cpl < 300) {
    return {
      variant: 'bad',
      message: 'Очень низкий максимальный CPL. Скорее всего маржа маленькая или конверсия продаж слишком низкая. Перепроверьте цифры.',
    };
  }

  if (crPct < 15) {
    return {
      variant: 'warn',
      message: 'Конверсия продаж <b>{crPct}%</b> — слабое место. Из 10 заявок покупает только <b>{buyers}</b>. Скрипт, скорость ответа и квалификация лида дадут больший прирост, чем рост бюджета.',
      vars: {
        crPct,
        cplAt30: cac * 0.3,
      },
    };
  }

  if (ltv >= 3) {
    return {
      variant: 'good',
      message: 'С учётом повторных покупок (×{ltv}) можно тратить на привлечение больше чем кажется. Ваш ROMI при текущем CPL: <b>{romiActual}%</b>.',
      vars: { ltv, romiActual },
    };
  }

  if (sharePct >= 40) {
    return {
      variant: 'warn',
      message: 'Вы отдаёте <b>{sharePct}%</b> маржи на привлечение — высоко для разовых клиентов. Убедитесь что операционные расходы покрываются оставшейся частью.',
      vars: { sharePct },
    };
  }

  return {
    variant: 'neutral',
    message: 'Для прибыли <b>{goal}</b> нужно <b>{salesNeeded} продаж</b> в месяц ({salesDaily}/день). Точка безубыточности <b>{bepSales} шт</b>. Запас прочности <b>{safety}</b> — есть риск при просадке продаж.',
    vars: { cpl, actual: actualCpl, romiActual },
  };
};
